<template>
  <BlBackdrop
    title="Previous recurring schedules"
    :label="`Add ${schedulesInput.length} schedule(s)`"
    :submitLoading="submitLoading"
    :disabled="isDisabledSubmit"
    @backdrop-close="$emit('close-modal')"
    @backdrop-action="onSubmit"
  >
    <div class="flex flex-col gap-2" :style="{ width: '40rem' }">
      <div class="flex flex-col gap-2 px-8">
        <div>
          <BlTextBody2
            text="Select schedules from recent records to include in the recurring appointments. You can modify them later if needed."
          />
        </div>
        <div class="flex items-center gap-2 pb-2 overflow-x-auto scrollbar-lg">
          <div
            v-for="day in dayOptions"
            :key="day"
            class="px-2 py-1 transition-all duration-300 rounded-full cursor-pointer hover:text-pure-white hover:bg-light-purple"
            :class="[
              dayInput === day
                ? 'text-pure-white bg-light-purple-400'
                : 'text-slate-800 bg-slate-200'
            ]"
            @click="onChangeDay(day)"
          >
            <span class="text-sm capitalize">
              {{ day }}
            </span>
          </div>
        </div>
      </div>
      <div
        class="px-8 overflow-y-auto"
        :style="{ maxHeight: 'calc(100vh - 24rem)' }"
      >
        <div v-if="fetchLoading" class="grid h-80 place-items-center">
          <div class="blum-loader"></div>
        </div>
        <div
          v-else-if="!previousSchedules.length"
          class="grid text-center h-80 place-items-center"
        >
          <BlTextBody2
            v-if="dayInput === 'all'"
            text="No recent schedules available. Once you create recurring appointments for this client, they will appear here."
          />
          <BlTextBody2
            v-else
            text="No recent schedules available for this day."
          />
        </div>
        <div v-else class="flex flex-col gap-3 min-h-80">
          <div
            v-for="schedule in previousSchedules"
            :key="schedule.id"
            class="flex gap-2 px-2 py-2 border rounded border-slate-400"
            :class="{ 'opacity-50': !schedule.is_available }"
          >
            <div class="pt-1">
              <BlInputCheck
                :checked="isChecked(schedule.id)"
                :disabled="!schedule.is_available"
                @changed="onChangeCheck(schedule)"
              />
            </div>
            <div class="flex flex-col gap-2">
              <div class="truncate w-132">
                <BlTextSubtitle3 :text="`Every ${schedule.day}`" />
                <BlTextSubtitle3
                  :text="
                    `${schedule.start_time_string} - ${schedule.end_time_string}`
                  "
                />
                <BlTextSubtitle3
                  :text="
                    (schedule.user && schedule.user.name) ||
                      'No therapist assigned'
                  "
                  class="truncate"
                />
              </div>
              <div class="flex items-center gap-3">
                <div class="flex items-center gap-2">
                  <div
                    v-if="enableBranch && schedule.room.branch"
                    class="flex items-center justify-center px-2 rounded h-7 max-w-32"
                    :style="{
                      backgroundColor:
                        schedule.room.branch.background_color || 'unset'
                    }"
                  >
                    <BlTextBody2
                      class="truncate"
                      :text="schedule.room.branch.name || '-'"
                      :style="{
                        color: schedule.room.branch.color || 'unset'
                      }"
                    />
                  </div>
                  <BlTextBody2
                    :text="schedule.room.name"
                    class="text-slate-700"
                  />
                </div>
                <div class="flex-shrink-0 w-1 h-1 rounded bg-slate-600"></div>
                <div>
                  <BlTextBody2
                    :text="schedule.total_credit"
                    class="text-slate-700"
                  />
                  <BlTextBody2 text="credit(s) of" class="text-slate-700" />
                  <BlTextBody2
                    v-if="schedule.credit_transaction_services.length"
                    :text="
                      schedule.credit_transaction_services[0].package_item_name
                    "
                    class="text-slate-700"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="totalPreviousSchedules > perPage" class="px-8">
        <BlInputPagination
          :page="page"
          :count="Math.ceil(totalPreviousSchedules / perPage)"
          @changed="changePage"
        />
      </div>
    </div>
  </BlBackdrop>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { displayDate } from "@/plugins/helper.js";
import moment from "moment";

import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlInputCheck from "../../BlInputCheck.vue";
import BlInputPagination from "../../BlInputPagination.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "PreviousRecurringSchedulesModal",
  components: {
    BlTextSubtitle3,
    BlTextBody2,
    BlInputCheck,
    BlInputPagination,
    BlBackdrop
  },
  props: {
    clientId: { type: Number, default: 0 }
    // $emit('close-modal')
    // $emit('after-commit')
  },
  data() {
    return {
      fetchLoading: false,
      submitLoading: false,
      previousSchedules: [],
      totalPreviousSchedules: 0,
      schedulesInput: [],
      //
      page: 1,
      perPage: 10,
      dayInput: "all",
      dayOptions: [
        "all",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday"
      ]
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "bookingAppointment",
      "bookingTimeslots"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    currentSlot() {
      const date = this.bookingTimeslots[0].date;
      const day = moment(date)
        .format("dddd")
        .toLowerCase();
      return { date, day };
    },
    isDisabledSubmit() {
      if (!this.schedulesInput.length) return true;
      return false;
    }
  },
  mounted() {
    this.fetchPreviousSchedules();
  },
  methods: {
    ...mapActions([
      "actionGet",
      "getBookingAppointment",
      "createRecurringAppointments",
      "createBookingTimeslot"
    ]),
    displayDate,
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    isChecked(id) {
      return this.schedulesInput.find(i => i.id === id) !== undefined;
    },
    onChangeCheck(schedule) {
      const id = schedule.id;
      if (this.isChecked(id)) {
        this.schedulesInput = this.schedulesInput.filter(i => i.id !== id);
      } else {
        this.schedulesInput.push(schedule);
      }
    },
    //
    async fetchPreviousSchedules() {
      const params = {
        url: `/api/v1/clients/${this.clientId}/previous_schedules?booking_appointment_id=${this.bookingAppointment.id}&page=${this.page}&per_page=${this.perPage}`
      };
      if (this.dayInput !== "all") {
        params.url += `&day=${this.dayInput}`;
      }

      this.fetchLoading = true;
      const { success, data } = await this.actionGet(params);
      this.fetchLoading = false;
      if (!success) return;

      this.previousSchedules = data.schedules.map(i => {
        const id = JSON.stringify(i);
        return { ...i, id };
      });
      this.totalPreviousSchedules = data.total_count;
    },
    onChangeDay(day) {
      if (day === this.dayInput) return;
      this.dayInput = day;
      this.fetchPreviousSchedules();
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchPreviousSchedules();
    },
    //
    async onSubmit() {
      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday"
      ];

      let arr = this.schedulesInput.map(i => {
        const day = i.day.toLowerCase();
        const index = days.findIndex(i => i === day);
        const currentIndex = days.findIndex(i => i === this.currentSlot.day);
        const add = (index + 7 - currentIndex) % 7;
        const startDate = moment(this.currentSlot.date)
          .add(add, "days")
          .format("YYYY-MM-DD");

        const supervisorIds = i.supervisors ? i.supervisors.map(i => i.id) : [];
        const packageItemId = i.credit_transaction_services.length
          ? i.credit_transaction_services[0].package_item_id
          : null;
        return {
          ...i,
          client_id: this.clientId,
          user_id: i.user.id,
          room_id: i.room.id,
          start_date: startDate,
          start_time: i.start_time_string,
          end_time: i.end_time_string,
          supervisor_ids: supervisorIds,
          package_item_id: packageItemId
        };
      });

      const params = {
        params: {
          booking_appointment_id: this.bookingAppointment.id,
          booking_in_review: true,
          from_schedules: true,
          data_appointments: arr
        },
        title: null,
        message: null,
        hideToast: true
      };

      this.submitLoading = true;
      await this.createRecurringAppointments(params);
      await this.getBookingAppointment({ id: this.bookingAppointment.id });
      this.submitLoading = false;

      this.$emit("after-commit");
      this.$emit("close-modal");
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/modal/PreviousRecurringSchedulesModal.vue